import { useState, useEffect, useRef } from "react";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Gift,
  Star,
  Check,
  ArrowRight,
  Clock,
  Instagram,
  Facebook,
  Sparkles,
  Users,
  Globe,
  Palette,
  BookOpen,
  Baby,
  Volume2,
  VolumeX,
} from "lucide-react";
import { motion } from "framer-motion";
import LandingNavigation from "@/components/LandingNavigation";
import Footer from "@/components/ui/footer";

// Use public assets that are committed to the repository
const promoImage1 = "/og-image.jpg";
// Video is optional - will be hidden if not available
const promoVideo: string | null = null;

export default function BlackFridayPage() {
  const [, navigate] = useLocation();
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [autoRedirectCountdown, setAutoRedirectCountdown] = useState(30);
  const [isPaused, setIsPaused] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleToggleSound = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  useEffect(() => {
    const endDate = new Date("December 1, 2025 23:59:59").getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = endDate - now;

      if (distance > 0) {
        setCountdown({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor(
            (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
          ),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000),
        });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isPaused) return;

    const redirectInterval = setInterval(() => {
      setAutoRedirectCountdown((prev) => {
        if (prev <= 1) {
          setShouldRedirect(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(redirectInterval);
  }, [isPaused]);

  useEffect(() => {
    if (shouldRedirect) {
      navigate("/auth?tab=register&promo=blackfriday20");
    }
  }, [shouldRedirect, navigate]);

  const handleSignUp = () => {
    navigate("/auth?tab=register&promo=blackfriday20");
  };

  return (
    <>
      <LandingNavigation />
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-pink-900 text-white overflow-hidden pt-16">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-yellow-400 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-40 right-20 w-48 h-48 bg-pink-500 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/3 w-40 h-40 bg-purple-400 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        <div className="relative z-10">
          <div className="py-4 px-4 sm:px-6 flex justify-center">
            <Badge className="bg-gradient-to-r from-red-600 to-pink-600 text-white text-sm sm:text-base px-4 py-2 animate-pulse shadow-lg">
              BLACK FRIDAY SPECIAL
            </Badge>
          </div>

          <section className="py-8 sm:py-12 px-4">
            <div className="container mx-auto max-w-6xl">
              <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-8"
              >
                <Badge className="mb-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold text-lg px-6 py-2">
                  LIMITED TIME OFFER
                </Badge>
                <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
                  <span className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-500 bg-clip-text text-transparent">
                    New SnuggleCrafters Families:
                  </span>
                  <br />
                  <span className="text-white">Get 20% Off + Enter to Win a Free Year</span>
                </h1>
              </motion.div>

              <div className="grid lg:grid-cols-2 gap-8 items-center mb-12">
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="order-2 lg:order-1"
                >
                  <img
                    src={promoImage1}
                    alt="SnuggleCrafters Black Friday Promo"
                    className="rounded-2xl shadow-2xl w-full max-w-md mx-auto border-4 border-yellow-400/30"
                    data-testid="img-promo-1"
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="order-1 lg:order-2"
                >
                  <div className="relative max-w-md mx-auto">
                    {promoVideo ? (
                      <>
                        <video
                          ref={videoRef}
                          src={promoVideo}
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="rounded-2xl shadow-2xl w-full border-4 border-pink-400/30"
                          data-testid="video-promo"
                        />
                        <button
                          onClick={handleToggleSound}
                          className={`absolute bottom-4 right-4 p-3 rounded-full transition-all duration-300 ${
                            isMuted 
                              ? "bg-white/90 text-gray-800 hover:bg-white animate-pulse" 
                              : "bg-pink-500 text-white hover:bg-pink-600"
                          }`}
                          aria-label={isMuted ? "Unmute video" : "Mute video"}
                          data-testid="button-toggle-sound"
                        >
                          {isMuted ? (
                            <VolumeX className="h-6 w-6" />
                          ) : (
                            <Volume2 className="h-6 w-6" />
                          )}
                        </button>
                        {isMuted && (
                          <div className="absolute bottom-4 right-20 bg-white/90 text-gray-800 px-3 py-1.5 rounded-full text-sm font-medium shadow-lg">
                            Tap to hear sound
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="rounded-2xl shadow-2xl w-full border-4 border-pink-400/30 bg-gradient-to-br from-purple-600 to-pink-600 p-8 flex flex-col items-center justify-center min-h-[300px]">
                        <Sparkles className="h-16 w-16 text-yellow-400 mb-4" />
                        <h3 className="text-2xl font-bold text-white text-center mb-2">20% Off Premium</h3>
                        <p className="text-white/80 text-center">Limited Time Black Friday Offer</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-center mb-12"
              >
                <p className="text-xl sm:text-2xl text-gray-200 max-w-3xl mx-auto mb-8 leading-relaxed">
                  Sign up for a free trial at SnuggleCrafters.com and enjoy{" "}
                  <span className="text-yellow-400 font-bold">20% off</span> the annual
                  Premium subscription when you upgrade. Plus, follow us on Instagram or
                  Facebook for a chance to win!
                </p>

                <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto mb-10">
                  <Card className="bg-gradient-to-br from-purple-800/80 to-pink-800/80 border-purple-500/30 text-white">
                    <CardContent className="p-6">
                      <Gift className="h-12 w-12 text-yellow-400 mb-4 mx-auto" />
                      <h3 className="text-xl font-bold mb-2" data-testid="text-prize-1">
                        One Full Year
                      </h3>
                      <p className="text-gray-300">
                        SnuggleCrafters Premium subscription
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-purple-800/80 to-pink-800/80 border-purple-500/30 text-white">
                    <CardContent className="p-6">
                      <Palette className="h-12 w-12 text-pink-400 mb-4 mx-auto" />
                      <h3 className="text-xl font-bold mb-2" data-testid="text-prize-2">
                        Unlimited Character Studio
                      </h3>
                      <p className="text-gray-300">
                        Create endless cartoon characters + illustrations
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-lg rounded-3xl p-6 sm:p-10 mb-12 border border-white/20"
              >
                <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 text-yellow-400">
                  How to Enter:
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-black shadow-lg">
                      1
                    </div>
                    <h3 className="text-xl font-semibold mb-2" data-testid="text-step-1">
                      Sign Up Free
                    </h3>
                    <p className="text-gray-300">
                      Start your free trial at SnuggleCrafters.com
                    </p>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white shadow-lg">
                      2
                    </div>
                    <h3 className="text-xl font-semibold mb-2" data-testid="text-step-2">
                      Follow Us
                    </h3>
                    <p className="text-gray-300 mb-3">
                      Follow @snugglecrafters on Instagram or Facebook
                    </p>
                    <div className="flex justify-center gap-4">
                      <a
                        href="https://www.instagram.com/snugglecrafters"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-gradient-to-br from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
                        data-testid="link-instagram"
                      >
                        <Instagram className="h-5 w-5" />
                        Instagram
                      </a>
                      <a
                        href="https://www.facebook.com/share/1Br2Ja6xoi"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 px-4 py-2 rounded-full text-sm font-medium transition-all hover:scale-105"
                        data-testid="link-facebook"
                      >
                        <Facebook className="h-5 w-5" />
                        Facebook
                      </a>
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold text-white shadow-lg">
                      3
                    </div>
                    <h3 className="text-xl font-semibold mb-2" data-testid="text-step-3">
                      You're Entered!
                    </h3>
                    <p className="text-gray-300">Automatically entered to win</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="text-center mb-12"
              >
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600/20 to-pink-600/20 border border-red-500/30 rounded-full px-6 py-3 mb-6">
                  <Clock className="h-5 w-5 text-red-400" />
                  <span className="text-lg font-medium">
                    Winner selected on{" "}
                    <span className="text-yellow-400 font-bold">December 1st</span>
                  </span>
                </div>

                <div className="grid grid-cols-4 gap-4 max-w-md mx-auto mb-8">
                  <div className="bg-gradient-to-br from-purple-700/60 to-pink-700/60 rounded-xl p-4 border border-purple-500/30">
                    <div
                      className="text-3xl sm:text-4xl font-bold text-yellow-400"
                      data-testid="text-countdown-days"
                    >
                      {countdown.days}
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">
                      Days
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-700/60 to-pink-700/60 rounded-xl p-4 border border-purple-500/30">
                    <div
                      className="text-3xl sm:text-4xl font-bold text-yellow-400"
                      data-testid="text-countdown-hours"
                    >
                      {countdown.hours}
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">
                      Hours
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-700/60 to-pink-700/60 rounded-xl p-4 border border-purple-500/30">
                    <div
                      className="text-3xl sm:text-4xl font-bold text-yellow-400"
                      data-testid="text-countdown-minutes"
                    >
                      {countdown.minutes}
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">
                      Mins
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-700/60 to-pink-700/60 rounded-xl p-4 border border-purple-500/30">
                    <div
                      className="text-3xl sm:text-4xl font-bold text-yellow-400"
                      data-testid="text-countdown-seconds"
                    >
                      {countdown.seconds}
                    </div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">
                      Secs
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1 }}
                className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-3xl p-6 sm:p-8 mb-12"
              >
                <h3 className="text-2xl font-bold text-center mb-6 text-yellow-400">
                  What You Get with Premium:
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { icon: BookOpen, text: "Unlimited customizable stories" },
                    { icon: Sparkles, text: "Cozy illustrations" },
                    { icon: Globe, text: "Multilingual narration" },
                    { icon: Baby, text: "Child profiles" },
                    { icon: Star, text: "Monthly magic updates" },
                    { icon: Users, text: "Family sharing" },
                  ].map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 text-gray-200"
                      data-testid={`feature-${index}`}
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <feature.icon className="h-5 w-5 text-black" />
                      </div>
                      <span>{feature.text}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 1.2 }}
                className="text-center mb-12"
              >
                <div className="bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-400/30 rounded-xl p-4 mb-6 inline-block">
                  <p className="text-sm text-gray-300">
                    Redirecting to sign up in{" "}
                    <span className="text-yellow-400 font-bold text-lg">
                      {autoRedirectCountdown}
                    </span>{" "}
                    seconds...
                    <button
                      onClick={() => setIsPaused(!isPaused)}
                      className="ml-3 text-pink-400 hover:text-pink-300 underline text-sm"
                      data-testid="button-pause-redirect"
                    >
                      {isPaused ? "Resume" : "Pause"}
                    </button>
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    size="lg"
                    onClick={handleSignUp}
                    className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-400 hover:to-orange-400 text-black font-bold text-lg px-10 py-6 shadow-2xl hover:shadow-yellow-500/30 transition-all hover:scale-105"
                    data-testid="button-start-trial"
                  >
                    Start Your Free Trial Now
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white/30 bg-transparent hover:bg-white/10 text-white text-lg px-8 py-6"
                    onClick={() => navigate("/pricing")}
                    data-testid="button-view-pricing"
                  >
                    View All Plans
                  </Button>
                </div>

                <p className="text-gray-400 mt-6 text-sm">
                  <Check className="inline h-4 w-4 text-green-400 mr-1" />
                  7-day free trial • No credit card required • Cancel anytime
                </p>
              </motion.div>

              <div className="py-6 text-center">
                <div className="flex flex-wrap justify-center gap-2 text-sm text-gray-400">
                  {[
                    "#SnuggleCrafters",
                    "#FamilyMagic",
                    "#StorytimeFun",
                    "#KidsStories",
                    "#AIStories",
                    "#CharacterStudio",
                    "#ParentLife",
                    "#Giveaway",
                    "#HolidayGiveaway",
                    "#BedtimeStories",
                  ].map((tag) => (
                    <span
                      key={tag}
                      className="bg-white/5 px-3 py-1 rounded-full hover:bg-white/10 transition-colors"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
}
